import './edit-retailer.scss';
import Header from '../../../organisms/header/header.js';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../utils/decode-token';
import { Alert, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from '../../../atoms/input';
import Textarea from '../../../atoms/textarea';
import apiClient from '../../../../utils/apiClient';
import { useForm } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import BackButton from '../../../atoms/back-button';

function EditRetailer() {
  const { t, i18n } = useTranslation();

  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const navigate = useNavigate();
  const location = useLocation();
  const { breadcrumbName, retailer: retailerFromState } = location.state || {};
  const { retailerId } = useParams();

  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [retailerData, setRetailerData] = useState(retailerFromState || null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [loadingRetailer, setLoadingRetailer] = useState(true);

  const [phoneAreaCode] = useState(() => {
    const user = JSON.parse(sessionStorage.getItem('loggedUser'));
    return user?.area_code?.toLowerCase() || '';
  });

  const regionNames = useMemo(() => {
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      const lang = i18n.resolvedLanguage || i18n.language || 'en';
      return new Intl.DisplayNames([lang], { type: 'region' });
    }
    return null;
  }, [i18n.resolvedLanguage, i18n.language]);

  const normalizeAreaCode = (code) => (code === 'LUX' ? 'LU' : code);
  const blockedCodes = useMemo(() => new Set(['EN']), []);

  const visibleCountries = useMemo(() => {
    return countries
      .filter((c) => c?.area_code && !blockedCodes.has(c.area_code))
      .map((c) => ({ ...c, area_code: normalizeAreaCode(c.area_code) }));
  }, [countries, blockedCodes]);

  const safeCountryLabel = (code, apiName) => {
    const iso2 = typeof code === 'string' ? code.toUpperCase() : '';
    if (regionNames && /^[A-Z]{2}$/.test(iso2)) {
      try {
        const localized = regionNames.of(iso2);
        if (localized) return localized;
      } catch {
        // ignore
      }
    }
    return apiName || code || '';
  };

  const schema = Joi.object({
    name: Joi.string().max(50).required().messages({
      'string.empty': t('alert-name'),
    }),
    retailer_code: Joi.string().max(50).required().messages({
      'string.empty': t('alert-retailer-code'),
    }),
    email: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
      'string.email': t('alert-email-format'),
      'string.empty': t('alert-email'),
    }),
    prefix_phone: Joi.string().pattern(/^\+[0-9]{2,3}$/).required().messages({
      'string.pattern.base': t('alert-prefix-format'),
      'string.empty': t('alert-prefix'),
    }),
    phone: Joi.string().pattern(/^[0-9]{7,15}$/).required().messages({
      'string.pattern.base': t('alert-phone-format'),
      'string.empty': t('alert-phone'),
    }),
    address_line_1: Joi.string().max(150).required().messages({
      'string.empty': t('alert-street-address'),
      'string.max': t('alert-address-max'),
    }),
    city: Joi.string().required().messages({
      'string.empty': t('alert-city'),
    }),
    area_code: Joi.string().required().messages({
      'string.empty': t('alert-country'),
    }),
    postal_code: Joi.string().required().messages({
      'string.empty': t('alert-zip'),
    }),
    preferred_language:
      languages.length > 1
        ? Joi.string().required().messages({
            'string.empty': t('alert-language'),
            'any.required': t('alert-language'),
          })
    : Joi.string().allow('').optional(),
    privacy_policy: Joi.string().max(255).optional().allow('').messages({}),
    terms_and_conditions: Joi.string().max(255).allow('').optional().messages({}),
    imprint_details: Joi.string().max(255).allow('').optional().messages({}),
    imprint_email: Joi.string().max(255).allow('').optional().messages({}),
  });

  const {
    register,
    handleSubmit,
    formState,
    setFocus,
    setValue,
    reset,
    getValues,
    watch,
  } = useForm({
    resolver: joiResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      retailer_code: '',
      email: '',
      prefix_phone: '',
      phone: '',
      address_line_1: '',
      city: '',
      area_code: '',
      postal_code: '',
      preferred_language: '',
      privacy_policy: '',
      terms_and_conditions: '',
      imprint_details: '',
      imprint_email: '',
    },
  });

  const { errors } = formState;
  const selectedLanguage = watch('preferred_language');

  const handleSnackbarClose = () => setSnackbarOpen(false);

  useEffect(() => {
    if (blockedCodes.has(selectedCountry)) {
      setSelectedCountry('');
      setValue('area_code', '');
    }
  }, [selectedCountry, blockedCodes, setValue]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get(`${process.env.REACT_APP_API_URL}/hac/countries`);
        setCountries(response.data);
      } catch {
        setSnackbarSeverity('error');
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarOpen(true);
      }
    };

    fetchCountries();
  }, [t]);

  useEffect(() => {
    const fetchRetailer = async () => {
      try {
        setLoadingRetailer(true);

        const response = await apiClient.get(
          `${process.env.REACT_APP_API_URL}/retailer?retailerId=${retailerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache',
            },
          }
        );

        const r = response?.data?.retailer;

        if (!r) {
          throw new Error('Retailer not found in response');
        }

        setRetailerData(r);
      } catch {
        if (retailerFromState) {
          setRetailerData(retailerFromState);
        } else {
          setSnackbarSeverity('error');
          setSnackbarMessage(t('common-internal-server-error'));
          setSnackbarOpen(true);
        }
      } finally {
        setLoadingRetailer(false);
      }
    };

    if (token && retailerId) {
      fetchRetailer();
    }
  }, [retailerId, token, retailerFromState, t]);

  useEffect(() => {
    if (!retailerData) return;

    setPhone(`${retailerData.prefix_phone || ''}${retailerData.phone || ''}`);
    setSelectedCountry(normalizeAreaCode(retailerData.area_code) || '');

    reset({
      name: retailerData.name || '',
      retailer_code: retailerData.retailer_code || '',
      email: retailerData.email || '',
      prefix_phone: retailerData.prefix_phone || '',
      phone: retailerData.phone || '',
      address_line_1: retailerData.address || '',
      city: retailerData.city || '',
      area_code: normalizeAreaCode(retailerData.area_code) || '',
      postal_code: retailerData.postal_code || '',
      preferred_language: retailerData.preferred_language || '',
      privacy_policy: retailerData.privacy_policy || '',
      terms_and_conditions: retailerData.terms_and_conditions || '',
      imprint_details: retailerData.imprint_details || '',
      imprint_email: retailerData.imprint_email || '',
    });
  }, [retailerData, reset]);

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setSelectedCountry(value);
    setValue('area_code', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (!countries.length || !selectedCountry) {
      setLanguages([]);
      setValue('preferred_language', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const country = countries.find(
      (item) => normalizeAreaCode(item.area_code) === selectedCountry
    );

    if (!country) {
      setLanguages([]);
      setValue('preferred_language', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const availableLanguages = (country.language || []).filter(
      (lang) => lang !== 'en'
    );

    setLanguages(availableLanguages);

    const currentPreferredLanguage = getValues('preferred_language');

    if (!currentPreferredLanguage || !availableLanguages.includes(currentPreferredLanguage)) {
      setValue('preferred_language', availableLanguages[0] || '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [countries, selectedCountry, setValue, getValues]);

  const onSubmit = async (data) => {
    try {
      const fullPhoneParsed = parsePhoneNumberFromString(phone);

      if (!fullPhoneParsed || !fullPhoneParsed.isValid()) {
        setPhoneError(t('alert-phone-format'));
        setFocus('phone');
        return;
      }

      setPhoneError('');

      const payload = {
        retailer_code: data.retailer_code,
        name: data.name,
        email: data.email,
        prefix_phone: `+${fullPhoneParsed.countryCallingCode}`,
        phone: fullPhoneParsed.nationalNumber,
        address: data.address_line_1,
        city: data.city,
        area_code: data.area_code,
        postal_code: data.postal_code,
        preferred_language: data.preferred_language || undefined,
        privacy_policy:
          data.area_code === 'IT' && data.privacy_policy ? data.privacy_policy : undefined,
        terms_and_conditions:
          data.area_code === 'IT' && data.terms_and_conditions
            ? data.terms_and_conditions
            : undefined,
        imprint_details:
          data.area_code === 'IT' && data.imprint_details ? data.imprint_details : undefined,
        imprint_email:
          data.area_code === 'IT' && data.imprint_email ? data.imprint_email : undefined,
      };

      const response = await apiClient.patch(
        `${process.env.REACT_APP_API_URL}/retailer/${retailerId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 204) {
        setSnackbarMessage(t('alert-data-saved'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSnackbarMessage(t('alert-duplicate'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const onError = () => {};

  return (
    <>
      {token && (role === 'admin' || role === 'marketAdmin' || role === 'countryAdmin') ? (
        <div className="App">
          <Header />
          <main>
            <section className="form">
              <div className="inner">
                {loadingRetailer ? (
                  <div>Loading...</div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit, onError)}>
                    <BackButton fallbackTo="/admin/main/retailer" context={breadcrumbName} />

                    <h2>{t('retailer-info-title')}</h2>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="name"
                            label={t('common-name')}
                            error={errors?.name}
                            {...register('name')}
                          />
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="retailer_code"
                            label={t('common-retailer-code')}
                            error={errors?.retailer_code}
                            {...register('retailer_code')}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="email"
                            label={t('common-email')}
                            error={errors?.email}
                            {...register('email')}
                          />
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <div className="form-control required">
                          <label htmlFor="phone">{t('common-phone')}</label>
                          <PhoneInput
                            defaultCountry={phoneAreaCode}
                            value={phone}
                            onChange={(value) => {
                              setPhone(value);
                              if (phoneTouched) {
                                const parsed = parsePhoneNumberFromString(value);
                                if (parsed && parsed.isValid()) {
                                  setPhoneError('');
                                  setValue('prefix_phone', `+${parsed.countryCallingCode}`, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  setValue('phone', parsed.nationalNumber, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                } else {
                                  setPhoneError(t('alert-phone-format'));
                                }
                              }
                            }}
                            onBlur={() => {
                              setPhoneTouched(true);
                              const parsed = parsePhoneNumberFromString(phone);
                              if (!parsed || !parsed.isValid()) {
                                setPhoneError(t('alert-phone-format'));
                              } else {
                                setPhoneError('');
                                setValue('prefix_phone', `+${parsed.countryCallingCode}`, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                                setValue('phone', parsed.nationalNumber, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }}
                          />
                          {phoneError && <span className="error-message">{phoneError}</span>}
                          <input type="hidden" {...register('prefix_phone')} />
                          <input type="hidden" {...register('phone')} />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 8, sm: 8, md: 12 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="150"
                            type="text"
                            id="address_line_1"
                            label={t('common-address')}
                            error={errors?.address_line_1}
                            {...register('address_line_1')}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                        <div className="form-control required">
                          <label htmlFor="country">{t('common-country')}</label>
                          <select
                            id="country"
                            className="normal"
                            {...register('area_code', {
                              onChange: handleCountryChange,
                            })}
                            value={selectedCountry || ''}
                          >
                            <option value="">{t('common-select-country')}</option>
                            {visibleCountries.map((country) => (
                              <option key={country.area_code} value={country.area_code}>
                                {safeCountryLabel(country.area_code, country.country)}
                              </option>
                            ))}
                          </select>
                          {errors.area_code && (
                            <span className="input__error">{errors.area_code.message}</span>
                          )}
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="city"
                            label={t('common-city')}
                            error={errors?.city}
                            {...register('city')}
                          />
                        </div>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                        <div className="form-control required">
                          <Input
                            maxLength="50"
                            type="text"
                            id="postal_code"
                            label={t('common-zip-cp')}
                            error={errors?.postal_code}
                            {...register('postal_code')}
                          />
                        </div>
                      </Grid>
                    </Grid>

                    {languages.length > 1 && (
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 4, md: 4 }}>
                          <div className="form-control required">
                            <label htmlFor="preferred_language">
                              {t('common-language')}
                            </label>
                            <select
                              id="preferred_language"
                              className="normal"
                              {...register('preferred_language')}
                              value={selectedLanguage || ''}
                            >
                              <option value="">{t('common-select-language')}</option>
                              {languages.map((language) => (
                                <option key={language} value={language}>
                                  {t(`languages-${language}`)}
                                </option>
                              ))}
                            </select>
                            {errors.preferred_language && (
                              <span className="input__error">{errors.preferred_language.message}</span>
                            )}
                          </div>
                        </Grid>
                      </Grid>
                    )}

                    <br />

                    {selectedCountry === 'IT' && (
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <Textarea
                            maxLength="255"
                            type="textarea"
                            id="privacy_policy"
                            label={t('common-legal-privacy-policy')}
                            error={errors?.privacy_policy}
                            {...register('privacy_policy', { value: '' })}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <Input
                            maxLength="50"
                            type="text"
                            id="terms_and_conditions"
                            label={t('common-legal-terms')}
                            error={errors?.terms_and_conditions}
                            {...register('terms_and_conditions')}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <Input
                            maxLength="50"
                            type="text"
                            id="imprint_details"
                            label={t('common-legal-imprint-details')}
                            error={errors?.imprint_details}
                            {...register('imprint_details')}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                          <Input
                            maxLength="50"
                            type="text"
                            id="imprint_email"
                            label={t('common-legal-imprint-email')}
                            error={errors?.imprint_email}
                            {...register('imprint_email')}
                          />
                        </Grid>
                      </Grid>
                    )}

                    <br />

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <button className="btn" type="submit">
                          {t('common-save')}
                        </button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </div>
            </section>
          </main>

          <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
}

export default EditRetailer;